/**
 * Design tokens – Professional, restrained aesthetic
 * Inspired by Systembolaget.se: clean whites, dark green accents, neutral grays
 */

export const colors = {
  // Base – neutral, professional
  text: '#1a1a1a',
  textSecondary: '#4a4a4a',
  textMuted: '#767676',

  // Backgrounds – clean whites and light grays
  white: '#ffffff',
  bgLight: '#f5f5f5',
  bgCard: '#ffffff',
  bgWarm: '#f0f0f0',

  // Borders – neutral gray
  border: '#d9d9d9',
  borderLight: '#e8e8e8',

  // Interactive – dark green (Systembolaget-inspired)
  primary: '#1a6b3a',
  primaryHover: '#145a30',

  // Answer buttons – professional, muted
  yes: '#2e7d4f',            // Confident green
  yesHover: '#256a42',
  no: '#8c8c8c',             // Neutral gray for "not relevant"
  noHover: '#737373',
  cart: '#b8860b',           // Muted gold/amber for "add to bag"
  cartHover: '#9a7209',

  // Accents
  focus: '#1a6b3a',
  accent: '#e8c547',
  accentLight: '#fdf8e8',
} as const;

export const fonts = {
  family: 'Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',

  // Sizes
  xs: '0.75rem',    // 12px
  sm: '0.875rem',   // 14px
  base: '1rem',     // 16px
  lg: '1.125rem',   // 18px
  xl: '1.25rem',    // 20px
  '2xl': '1.5rem',  // 24px
  '3xl': '1.875rem', // 30px

  // Weights
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700,

  // Line heights
  lineHeightTight: 1.25,
  lineHeightNormal: 1.5,
  lineHeightRelaxed: 1.625,
} as const;

export const spacing = {
  px: '1px',
  0: '0',
  0.5: '0.125rem',  // 2px
  1: '0.25rem',     // 4px
  2: '0.5rem',      // 8px
  3: '0.75rem',     // 12px
  4: '1rem',        // 16px
  5: '1.25rem',     // 20px
  6: '1.5rem',      // 24px
  8: '2rem',        // 32px
  10: '2.5rem',     // 40px
  12: '3rem',       // 48px
  16: '4rem',       // 64px
} as const;

export const radii = {
  none: '0',
  sm: '0.25rem',    // 4px
  md: '0.375rem',   // 6px
  lg: '0.5rem',     // 8px
  xl: '0.75rem',    // 12px
  full: '9999px',
} as const;

export const shadows = {
  none: 'none',
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.07), 0 2px 4px -2px rgba(0, 0, 0, 0.05)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -4px rgba(0, 0, 0, 0.05)',
} as const;

export const transitions = {
  fast: '150ms ease',
  normal: '200ms ease',
  slow: '300ms ease',
} as const;

export const layout = {
  maxWidth: '1200px',
  contentWidth: '720px',
  headerHeight: '64px',
  minTouchTarget: '44px',
} as const;

export const breakpoints = {
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
} as const;

export const buttonBase = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: fonts.medium,
  fontSize: fonts.base,
  lineHeight: fonts.lineHeightTight,
  borderRadius: radii.md,
  transition: `all ${transitions.fast}`,
  cursor: 'pointer',
  border: 'none',
  outline: 'none',
  minHeight: layout.minTouchTarget,
  padding: `${spacing[3]} ${spacing[6]}`,
} as const;

export const theme = {
  colors,
  fonts,
  spacing,
  radii,
  shadows,
  transitions,
  layout,
  breakpoints,
  buttonBase,
} as const;

export default theme;
